/**
 * CihArticleReader — narrow-column reader shell for article pages. Handles
 * the header (kicker, h1, description, byline, meta strip) and wraps the
 * rich-text body in reader-friendly typography.
 *
 * Aliases: article reader, article shell, long-form wrapper
 */

import { Fragment, type ReactNode } from "react";

import { ProfileLinkOrPlain } from "~/components/cih/article-coach-links";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { SafeLink } from "~/components/ui/safe-link";

type BreadcrumbItem = { label: string; href?: string };

export type ArticleMetaStripEntry = {
  readonly text: string;
  readonly href?: string;
};

export type ArticleMetaStripItem = {
  readonly label: string;
  readonly entries: ReadonlyArray<ArticleMetaStripEntry>;
};

export type ArticleAuthor =
  | {
      readonly kind: "coach";
      readonly username: string;
      /** False when the coach's profile page 404s: byline shows, no link. */
      readonly hasPublicProfile: boolean;
      readonly fullName: string;
      readonly profilePictureUrl: string | null;
      readonly credential: string | null;
    }
  | {
      readonly kind: "freeText";
      readonly name: string;
      readonly title: string | null;
    };

type Props = {
  readonly breadcrumb: BreadcrumbItem[];
  readonly kicker: string;
  readonly title: string;
  readonly description: string;
  readonly author: ArticleAuthor;
  readonly metaStrip: ReadonlyArray<ArticleMetaStripItem>;
  readonly publishedAt?: string;
  readonly children: ReactNode;
};

function CoachByline({
  author,
  readMinutes,
}: {
  readonly author: Extract<ArticleAuthor, { kind: "coach" }>;
  readonly readMinutes: number | null;
}) {
  const content = (
    <>
      {author.profilePictureUrl ? (
        <img
          src={author.profilePictureUrl}
          alt=""
          width={36}
          height={36}
          className="h-9 w-9 rounded-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="h-9 w-9 rounded-full bg-gray-200" />
      )}
      <span>
        By{" "}
        <strong className="font-medium text-gray-900">{author.fullName}</strong>
        {author.credential ? ` · ${author.credential}` : ""}
        {readMinutes ? ` · ${readMinutes} min read` : ""}
      </span>
    </>
  );

  return (
    <ProfileLinkOrPlain
      coach={author}
      className="flex items-center gap-3"
      linkClassName="hover:text-gray-900"
    >
      {content}
    </ProfileLinkOrPlain>
  );
}

export function CihArticleReader({
  breadcrumb,
  kicker,
  title,
  description,
  author,
  metaStrip,
  publishedAt,
  children,
}: Props) {
  const readMinutes = estimateReadingTime(children);

  return (
    <article className="bg-stone-50 pt-6 pb-16">
      <div className="mx-auto max-w-3xl px-4 pb-6 md:pb-8">
        <Breadcrumb items={breadcrumb} isBold={false} />
        <div className="mt-6 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
          ● {kicker}
        </div>
        <h1 className="mt-4 font-heading text-4xl font-medium tracking-tight text-gray-900 md:text-5xl">
          {title}
        </h1>
        <p className="mt-4 text-lg leading-normal text-gray-700">
          {description}
        </p>
        <div className="mt-6 flex items-center gap-3 text-xs text-gray-500">
          {author.kind === "coach" ? (
            <CoachByline author={author} readMinutes={readMinutes} />
          ) : (
            <span>
              By{" "}
              <strong className="font-medium text-gray-900">
                {author.name}
              </strong>
              {author.title ? ` · ${author.title}` : ""}
              {readMinutes ? ` · ${readMinutes} min read` : ""}
            </span>
          )}
        </div>
        <div className="mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-2 border-y border-gray-200 py-3 text-[11px] tracking-wider text-gray-500 uppercase">
          {metaStrip.map((m) =>
            m.entries.length > 0 ? (
              <span key={m.label}>
                <span className="text-gray-400">{m.label}&nbsp;·&nbsp;</span>
                {m.entries.map((e, i) => (
                  <Fragment key={`${e.text}-${i}`}>
                    {i > 0 ? <span className="text-gray-400">, </span> : null}
                    {e.href ? (
                      <SafeLink
                        className="font-medium text-gray-900 hover:text-purple-700"
                        href={e.href}
                      >
                        {e.text}
                      </SafeLink>
                    ) : (
                      <span className="font-medium text-gray-900">
                        {e.text}
                      </span>
                    )}
                  </Fragment>
                ))}
              </span>
            ) : null,
          )}
          {publishedAt ? (
            <span>
              <span className="text-gray-400">Published&nbsp;·&nbsp;</span>
              <span className="font-medium text-gray-900">{publishedAt}</span>
            </span>
          ) : null}
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-4">
        <div className="prose prose-lg prose-headings:font-heading prose-headings:font-medium prose-headings:tracking-tight prose-a:text-purple-700 [&>p:first-of-type]:first-letter:float-left [&>p:first-of-type]:first-letter:pr-2 [&>p:first-of-type]:first-letter:font-heading [&>p:first-of-type]:first-letter:text-5xl [&>p:first-of-type]:first-letter:leading-none [&>p:first-of-type]:first-letter:font-medium">
          {children}
        </div>
      </div>
    </article>
  );
}

/** Very rough reading-time estimate based on React children text depth. Conservative: 250 wpm. */
function estimateReadingTime(_children: ReactNode): number | null {
  // Intentionally null; we don't want to walk the tree every render. Authors
  // can pass an override later if needed.
  return null;
}
